import { PERMISSION_MATRIX, } from './generated.js';
export const WORKSPACE_PERSONA_ROLES = [
    'SUPER_ADMIN',
    'ORG_ADMIN',
    'WORKSPACE_MANAGER',
    'CAMPAIGN_EDITOR',
    'COMPLIANCE_OFFICER',
    'VIEWER',
];
export const SPEC_V21_CAPABILITIES = [
    'manageBillingSsoAndWorkspaces',
    'configureCustomDomainsAndConnectors',
    'createGtinsUploadCsvAndBindRoutes',
    'triggerRecallAndLockRegulatedLinkTypes',
    'revertIdentifierRouteHistory',
    'exportBarcodesAndViewAnalytics',
    'manageApiKeysAndWebhooks',
    'manageMembershipsAndOrgAudit',
];
export const SPEC_V21_RBAC_MATRIX = {
    manageBillingSsoAndWorkspaces: {
        SUPER_ADMIN: true,
        ORG_ADMIN: true,
        WORKSPACE_MANAGER: false,
        CAMPAIGN_EDITOR: false,
        COMPLIANCE_OFFICER: false,
        VIEWER: false,
    },
    configureCustomDomainsAndConnectors: {
        SUPER_ADMIN: true,
        ORG_ADMIN: true,
        WORKSPACE_MANAGER: true,
        CAMPAIGN_EDITOR: false,
        COMPLIANCE_OFFICER: false,
        VIEWER: false,
    },
    createGtinsUploadCsvAndBindRoutes: {
        SUPER_ADMIN: true,
        ORG_ADMIN: true,
        WORKSPACE_MANAGER: true,
        CAMPAIGN_EDITOR: true,
        COMPLIANCE_OFFICER: false,
        VIEWER: false,
    },
    triggerRecallAndLockRegulatedLinkTypes: {
        SUPER_ADMIN: true,
        ORG_ADMIN: true,
        WORKSPACE_MANAGER: true,
        CAMPAIGN_EDITOR: false,
        COMPLIANCE_OFFICER: true,
        VIEWER: false,
    },
    revertIdentifierRouteHistory: {
        SUPER_ADMIN: true,
        ORG_ADMIN: true,
        WORKSPACE_MANAGER: true,
        CAMPAIGN_EDITOR: false,
        COMPLIANCE_OFFICER: true,
        VIEWER: false,
    },
    exportBarcodesAndViewAnalytics: {
        SUPER_ADMIN: true,
        ORG_ADMIN: true,
        WORKSPACE_MANAGER: true,
        CAMPAIGN_EDITOR: true,
        COMPLIANCE_OFFICER: true,
        VIEWER: true,
    },
    manageApiKeysAndWebhooks: {
        SUPER_ADMIN: true,
        ORG_ADMIN: true,
        WORKSPACE_MANAGER: true,
        CAMPAIGN_EDITOR: false,
        COMPLIANCE_OFFICER: false,
        VIEWER: false,
    },
    manageMembershipsAndOrgAudit: {
        SUPER_ADMIN: true,
        ORG_ADMIN: true,
        WORKSPACE_MANAGER: false,
        CAMPAIGN_EDITOR: false,
        COMPLIANCE_OFFICER: false,
        VIEWER: false,
    },
};
export const REGULATED_LINK_TYPES = ['gs1:epil', 'gs1:smpc', 'gs1:recallStatus'];
export const MAX_CUSTOM_CLAIMS_BYTES = 1000;
export const ROLE_TO_SHORT_CODE = {
    SUPER_ADMIN: 'SA',
    ORG_ADMIN: 'OA',
    WORKSPACE_MANAGER: 'WM',
    WORKSPACE_EDITOR: 'CE',
    CAMPAIGN_EDITOR: 'CE',
    COMPLIANCE_OFFICER: 'CO',
    WORKSPACE_VIEWER: 'VW',
    VIEWER: 'VW',
    PARTNER_API: 'CE',
};
export const SHORT_CODE_TO_PERSONA = {
    SA: 'SUPER_ADMIN',
    OA: 'ORG_ADMIN',
    WM: 'WORKSPACE_MANAGER',
    WE: 'CAMPAIGN_EDITOR',
    CE: 'CAMPAIGN_EDITOR',
    CO: 'COMPLIANCE_OFFICER',
    VW: 'VIEWER',
};
export function normalizeRoleName(role) {
    if (role === 'WORKSPACE_EDITOR')
        return 'CAMPAIGN_EDITOR';
    if (role === 'WORKSPACE_VIEWER')
        return 'VIEWER';
    return role;
}
export function mapPersonaToContractRole(persona) {
    return persona;
}
export function mapContractRoleToPersona(role) {
    const normalized = normalizeRoleName(role);
    switch (normalized) {
        case 'SUPER_ADMIN':
            return 'SUPER_ADMIN';
        case 'ORG_ADMIN':
            return 'ORG_ADMIN';
        case 'WORKSPACE_MANAGER':
            return 'WORKSPACE_MANAGER';
        case 'CAMPAIGN_EDITOR':
        case 'PARTNER_API':
            return 'CAMPAIGN_EDITOR';
        case 'COMPLIANCE_OFFICER':
            return 'COMPLIANCE_OFFICER';
        case 'VIEWER':
            return 'VIEWER';
    }
}
export function hasPartnerApiScope(caller, requiredScopes) {
    const scopes = caller.apiScopes ?? [];
    return requiredScopes.some((scope) => scopes.includes(scope));
}
export function canPerformCapability(caller, capability, targetOrgId, targetWorkspaceId) {
    const capDef = PERMISSION_MATRIX[capability];
    const callerRole = normalizeRoleName(caller.role);
    if (callerRole === 'SUPER_ADMIN') {
        return capDef.permissions.SUPER_ADMIN;
    }
    if (caller.orgId !== targetOrgId) {
        return false;
    }
    if (callerRole === 'PARTNER_API') {
        if (capability === 'domain:read') {
            return hasPartnerApiScope(caller, ['domains:read', 'domains:write']);
        }
        if (capability === 'domain:manage' || capability === 'domain:verify') {
            return hasPartnerApiScope(caller, ['domains:write']);
        }
        if (!capDef.permissions.PARTNER_API) {
            return false;
        }
        if (capDef.requiredScope !== undefined) {
            if (capDef.requiredScope === 'recalls:write') {
                return hasPartnerApiScope(caller, ['recalls:write', 'recall:write']);
            }
            if (capDef.requiredScope === 'analytics:read') {
                return hasPartnerApiScope(caller, ['analytics:read', 'analytics:write']);
            }
            return hasPartnerApiScope(caller, [capDef.requiredScope]);
        }
        return true;
    }
    if (callerRole === 'ORG_ADMIN') {
        return capDef.permissions.ORG_ADMIN;
    }
    if (targetWorkspaceId !== undefined) {
        const scopedRole = caller.workspaces?.[targetWorkspaceId];
        if (scopedRole === undefined) {
            return false;
        }
        return capDef.permissions[normalizeRoleName(scopedRole)];
    }
    return capDef.permissions[callerRole];
}
export function canPerformSpecCapability(caller, capability, targetOrgId, targetWorkspaceId) {
    const callerRole = normalizeRoleName(caller.role);
    if (callerRole === 'SUPER_ADMIN' || caller.personaRole === 'SUPER_ADMIN') {
        return SPEC_V21_RBAC_MATRIX[capability].SUPER_ADMIN;
    }
    if (caller.orgId !== targetOrgId) {
        return false;
    }
    if (callerRole === 'ORG_ADMIN' || caller.personaRole === 'ORG_ADMIN') {
        return SPEC_V21_RBAC_MATRIX[capability].ORG_ADMIN;
    }
    if (targetWorkspaceId !== undefined) {
        const scopedPersona = caller.workspacePersonas?.[targetWorkspaceId] ??
            (caller.workspaces?.[targetWorkspaceId] !== undefined
                ? mapContractRoleToPersona(caller.workspaces[targetWorkspaceId])
                : undefined);
        if (scopedPersona === undefined) {
            return false;
        }
        return SPEC_V21_RBAC_MATRIX[capability][scopedPersona];
    }
    const wsPersona = caller.personaRole ?? mapContractRoleToPersona(caller.role);
    return SPEC_V21_RBAC_MATRIX[capability][wsPersona];
}
//# sourceMappingURL=guards.js.map